import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {Flex, Form, Modal, Row, Space} from 'antd'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {useIntl} from 'react-intl'
import {deleteRequest, getRequest} from '../../../services/base'
import {NavContext} from '../../../context/global_context'
import useDebouncedValue from '../../../utils/common_function'
import {buildPageUrl, getDeleteTargetPage} from '../../../utils/common'
import InputText from '../../common/InputText'
import Button from '../../common/Button'
import {SELECT_SUB_SYSTEM, SET_SUB_SYSTEM_LIST, SubSystemContext,} from './context/context'
import SubSystemTable from './SubSystemTable'
import SubSystemFormModal from './form'
import { notifyError } from '../../../utils/notifications'

const SubSystem = () => {
  const intl = useIntl()
  const [state, dispatch] = useContext(SubSystemContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef('/api/sub_system/')

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const getSubSystemListData = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'subSystem.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_SUB_SYSTEM_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'subSystem.loadError' }))
    }
  }

  useEffect(() => {
    getSubSystemListData()
  }, [])

  useEffect(() => {
    const url = `/api/sub_system/?search=${search}`
    currentUrl.current = url
    getSubSystemListData()
  }, [debouncedSearch])

  const onCreateSubSystem = () => {
    dispatch({
      type: SELECT_SUB_SYSTEM,
      data: { selected_sub_system: {}, show_sub_system_popup: true },
    })
  }

  const onEditSubSystem = (record) => {
    dispatch({
      type: SELECT_SUB_SYSTEM,
      data: { selected_sub_system: record, show_sub_system_popup: true },
    })
  }

  const deleteSubSystem = async (record) => {
    let res = await deleteRequest(`/api/sub_system/${record.id}/`)
    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'subSystem.deleteSuccess' }),
        },
      })
      currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
      getSubSystemListData()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'subSystem.deleteError' }),
        },
      })
    }
  }

  const confirmDelete = (record) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'subSystem.confirmDelete' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'subSystem.confirmDeleteContent' },
        { name: record.name }
      ),
      onOk: () => deleteSubSystem(record),
      centered: true,
      maskClosable: true,
    })
  }

  const dataSource = useMemo(() => {
    if (!state?.sub_system_list?.length) return []
    return state.sub_system_list.map((item, index) => ({
      ...item,
      key: `sub-system-${index}`,
      rowKey: `sub-system-${item.id}`,
      index,
    }))
  }, [state.sub_system_list])

  return (
    <>
      {contextHolder}

      {state.show_sub_system_popup && (
        <SubSystemFormModal getSubSystemListData={getSubSystemListData} />
      )}

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width administration-form">
            <Flex justify="space-between" gap={16} className="full-width">
              <InputText
                name="search_sub_system"
                type="search"
                placeholder={intl.formatMessage({
                  id: 'subSystem.searchPlaceholder',
                })}
                value={search}
                isRequired={false}
                className="full-width"
                wrapperClass="full-width"
                onChange={(value) => setSearch(value)}
              />
              <Space size="middle" align="center">
                <Button onClick={onCreateSubSystem}>
                  {intl.formatMessage({ id: 'subSystem.createNew' })}
                </Button>
              </Space>
            </Flex>
          </Form>

          <Flex style={{ width: '100%' }}>
            <SubSystemTable
              dataSource={dataSource}
              pagination={pagination}
              getSubSystemListData={getSubSystemListData}
              onEdit={onEditSubSystem}
              onDelete={confirmDelete}
            />
          </Flex>
        </Row>
      </div>
    </>
  )
}

export default SubSystem
